import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Plus, Flame, Eye, ShieldCheck } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";

const catIcons = { chaleur: Flame, intimite: Eye, securite: ShieldCheck };

// Section interactive « Pour chaque problème, une solution » :
// catégories à gauche (onglets défilables sur mobile), 2 cartes photo + verre dépoli à droite.
export default function SolutionsSection() {
  const { t } = useLanguage();
  const data = t("solutions");
  const [active, setActive] = useState(0);
  const cat = data.categories[active];

  return (
    <section data-testid="solutions-section" className="py-16 sm:py-24 lg:py-32 bg-slate-950 text-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="max-w-3xl mb-10 lg:mb-14">
          <div className="text-xs tracking-[0.3em] uppercase font-display font-semibold text-[#C5A880]">{data.eyebrow}</div>
          <h2 className="font-display text-3xl lg:text-5xl font-bold tracking-tight leading-[1.1] mt-3">
            {data.title}
          </h2>
          <p className="mt-5 text-slate-300 text-lg leading-relaxed">{data.text}</p>
        </div>

        <div className="grid lg:grid-cols-[320px,1fr] gap-8 lg:gap-12">
          {/* Catégories, onglets défilables sur mobile */}
          <div
            data-testid="solutions-categories"
            className="flex lg:flex-col gap-2 overflow-x-auto lg:overflow-visible pb-2 lg:pb-0 -mx-6 px-6 lg:mx-0 lg:px-0"
          >
            {data.categories.map((c, i) => {
              const Icon = catIcons[c.id];
              const isActive = i === active;
              return (
                <button
                  key={c.id}
                  data-testid={`solutions-cat-${c.id}`}
                  onClick={() => setActive(i)}
                  aria-pressed={isActive}
                  className={`flex items-center justify-between gap-3 px-5 py-4 rounded-2xl border text-left whitespace-nowrap lg:whitespace-normal flex-shrink-0 lg:flex-shrink transition-all duration-300 ${
                    isActive
                      ? "bg-white/10 border-[#C5A880]/50 text-white shadow-lg shadow-black/20"
                      : "border-white/10 text-slate-400 hover:text-white hover:border-white/25"
                  }`}
                >
                  <span className="inline-flex items-center gap-3">
                    <Icon className="w-5 h-5 text-[#C5A880] flex-shrink-0" />
                    <span className="font-medium">{c.label}</span>
                  </span>
                  <Plus className={`w-4 h-4 flex-shrink-0 transition-transform duration-300 ${isActive ? "rotate-45 text-[#C5A880]" : "text-slate-500"}`} />
                </button>
              );
            })}
          </div>

          {/* Cartes, une par rangée sur mobile, deux sur grand écran */}
          <div key={cat.id} className={`grid gap-5 animate-fade-up ${cat.cards.length > 1 ? "sm:grid-cols-2" : "max-w-xl"}`}>
            {cat.cards.map((card, i) => (
              <div
                key={card.title}
                data-testid={`solutions-card-${i}`}
                className="relative rounded-3xl overflow-hidden min-h-[380px] flex items-end group"
              >
                <img
                  src={card.image}
                  alt={card.title}
                  loading="lazy"
                  decoding="async"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950/90 via-slate-950/30 to-transparent" />
                <div className="relative m-4 w-[calc(100%-2rem)] rounded-2xl bg-white/10 backdrop-blur-xl border border-white/15 p-6">
                  <h3 className="font-display text-xl font-bold text-white tracking-tight">{card.title}</h3>
                  <p className="mt-2 text-sm text-slate-200 leading-relaxed">{card.text}</p>
                  <Link
                    to={card.to}
                    data-testid={`solutions-more-${i}`}
                    className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-[#E2D3BE] hover:gap-3 transition-all"
                  >
                    {data.more}
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
